// components/loginforms/UsernameValidation.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import styles from '../Header.module.css';
import illegalUsernames from './illegalUsernames';

const UsernameValidation = ({ username, setUsername }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [validationResults, setValidationResults] = useState({
    isEmpty: true,
    isValidFormat: false,
    isValidLength: false,
    isNotProhibited: true,
    isAvailable: false,
    message: ''
  });

  // Check username whenever it changes
  useEffect(() => {
    const checkUsername = async () => {
      // Reset validation
      let newResults = {
        isEmpty: !username.trim(),
        isValidFormat: false,
        isValidLength: false,
        isNotProhibited: true,
        isAvailable: false,
        message: ''
      };

      // Don't check empty username
      if (newResults.isEmpty) {
        setValidationResults(newResults);
        return;
      }

      // Check format (only letters, numbers, and underscores)
      const usernameRegex = /^[a-zA-Z0-9_]+$/;
      newResults.isValidFormat = usernameRegex.test(username);
      
      if (!newResults.isValidFormat) {
        newResults.message = 'Username can only contain letters, numbers, and underscores';
        setValidationResults(newResults);
        return;
      }

      // Check length (3-20 characters)
      newResults.isValidLength = username.length >= 3 && username.length <= 20;
      
      if (!newResults.isValidLength) {
        newResults.message = 'Username must be between 3 and 20 characters';
        setValidationResults(newResults);
        return;
      }

      // Check against prohibited usernames
      const lowercaseUsername = username.toLowerCase();
      newResults.isNotProhibited = !illegalUsernames.some(prohibited => 
        lowercaseUsername === prohibited || 
        lowercaseUsername.includes(prohibited)
      );
      
      if (!newResults.isNotProhibited) {
        newResults.message = 'This username is not allowed';
        setValidationResults(newResults);
        return;
      }

      // All initial checks passed, now check if username is available
      setIsChecking(true);
      
      try {
        // Important change: Check the lowercase version of the username in Firestore
        const lowercaseUsername = username.trim().toLowerCase();
        const usernameDoc = await getDoc(doc(db, 'usernames', lowercaseUsername));
        newResults.isAvailable = !usernameDoc.exists();
        
        if (!newResults.isAvailable) {
          newResults.message = 'Username is already taken';
        } else {
          newResults.message = 'Username is available!';
        }
      } catch (error) {
        console.error('Error checking username:', error);
        newResults.message = 'Error checking username';
      }
      
      setValidationResults(newResults);
      setIsChecking(false);
    };
    
    // Use a timeout to avoid checking on every keystroke
    const timer = setTimeout(() => {
      checkUsername();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [username]);

  // Show nothing if username is empty and user hasn't interacted yet
  if (validationResults.isEmpty && !username) {
    return null;
  }

  // Show validation results
  return (
    <div className={styles.usernameValidation}>
      {isChecking ? (
        <p className={styles.checkingUsername}>Checking username...</p>
      ) : (
        <>
          {validationResults.message && (
            <p className={`${styles.validationMessage} ${
              validationResults.isAvailable ? styles.success : styles.error
            }`}>
              {validationResults.message}
            </p>
          )}
          
          <ul className={styles.requirements}>
            <li className={validationResults.isValidFormat ? styles.met : styles.notMet}>
              Letters, numbers, and underscores only
            </li>
            <li className={validationResults.isValidLength ? styles.met : styles.notMet}>
              Between 3 and 20 characters
            </li>
            <li className={validationResults.isNotProhibited ? styles.met : styles.notMet}>
              Not using prohibited words
            </li>
            <li 
              className={
                validationResults.isValidFormat && 
                validationResults.isValidLength && 
                validationResults.isNotProhibited &&
                validationResults.isAvailable 
                  ? styles.met 
                  : validationResults.isValidFormat && 
                    validationResults.isValidLength &&
                    validationResults.isNotProhibited &&
                    !isChecking
                      ? validationResults.isAvailable 
                        ? styles.met
                        : styles.notMet
                      : styles.checking
              }
            >
              {validationResults.isValidFormat && 
               validationResults.isValidLength && 
               validationResults.isNotProhibited &&
               !isChecking
                ? validationResults.isAvailable 
                  ? "Username available" 
                  : "Username already taken"
                : "Username must be available"}
            </li>
          </ul>
        </>
      )}
    </div>
  );
};

export default UsernameValidation;